import React, { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL;

// Helper to get valid admin access token, refresh if needed
async function getAdminAccessToken() {
  let accessToken = localStorage.getItem('gogig_admin_access');
  const refreshToken = localStorage.getItem('gogig_admin_refresh');
  if (accessToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (res.status !== 401) return accessToken;
    } catch {}
  }
  if (refreshToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      const data = await res.json();
      if (data.success && data.accessToken) {
        localStorage.setItem('gogig_admin_access', data.accessToken);
        return data.accessToken;
      }
    } catch {}
  }
  // Clear tokens and redirect to login instead of reloading
  localStorage.removeItem('gogig_admin_access');
  localStorage.removeItem('gogig_admin_refresh');
  window.location.href = '/admin'; // Redirect to admin login page
  return null;
}

async function adminFetch(url, options = {}) {
  const token = await getAdminAccessToken();
  if (!token) throw new Error('Not authenticated');
  const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
  return fetch(url, { ...options, headers });
}

function AdminReferralReport({ getToken, fetch: fetchProp, hideConfig = false }) {
  const getTokenFn = getToken || getAdminAccessToken;
  const fetchFn = fetchProp || adminFetch;
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Referral reward state (only used when !hideConfig)
  const [rewardAmount, setRewardAmount] = useState('');
  const [rewardLoading, setRewardLoading] = useState(false);
  const [rewardMsg, setRewardMsg] = useState('');

  // Dashboard videos state (only used when !hideConfig)
  const [videos, setVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoMsg, setVideoMsg] = useState('');
  const [videoLoading, setVideoLoading] = useState(false);

  useEffect(() => {
    if (!hideConfig) {
      fetchRewardAmount();
      fetchVideos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideConfig]);

  const fetchRewardAmount = async () => {
    setRewardLoading(true);
    setRewardMsg('');
    try {
      const res = await fetchFn(`${API_URL}/api/candidate/referral-reward`);
      const data = await res.json();
      if (data.success) {
        setRewardAmount(String(data.amount));
      } else {
        setRewardMsg('Failed to fetch reward amount');
      }
    } catch (e) {
      setRewardMsg('Failed to fetch reward amount');
    }
    setRewardLoading(false);
  };

  const saveRewardAmount = async () => {
    setRewardLoading(true);
    setRewardMsg('');
    try {
      const amt = parseInt(rewardAmount, 10);
      if (isNaN(amt) || amt < 0) {
        setRewardMsg('Please enter a valid amount');
        setRewardLoading(false);
        return;
      }
      const res = await fetchFn(`${API_URL}/api/admin/referral-reward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt })
      });
      const data = await res.json();
      if (data.success) {
        setRewardMsg('Reward amount updated!');
      } else {
        setRewardMsg(data.error || 'Failed to update');
      }
    } catch (e) {
      setRewardMsg('Failed to update reward amount');
    }
    setRewardLoading(false);
  };

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const params = [];
      if (month) params.push(`month=${month}`);
      if (year) params.push(`year=${year}`);
      const url = `${API_URL}/api/referral/report${params.length ? '?' + params.join('&') : ''}`;
      const res = await fetchFn(url);
      const data = await res.json();
      if (data.success) {
        setReport(data.report);
      } else {
        setError(data.error || 'Failed to fetch report');
      }
    } catch (err) {
      setError('Failed to fetch report');
    }
    setLoading(false);
  };

  const handleDownloadCSV = async () => {
    const params = [];
    if (month) params.push(`month=${month}`);
    if (year) params.push(`year=${year}`);
    params.push('format=csv');
    const url = `${API_URL}/api/referral/report?${params.join('&')}`;
    const token = await getTokenFn();
    window.open(url + `&token=${token}`, '_blank');
  };

  const fetchVideos = async () => {
    setVideoLoading(true);
    setVideoMsg('');
    try {
      const res = await fetchFn(`${API_URL}/api/admin/dashboard-videos`);
      const data = await res.json();
      if (data.success) setVideos(data.videos);
      else setVideoMsg('Failed to fetch videos');
    } catch (e) {
      setVideoMsg('Failed to fetch videos');
    }
    setVideoLoading(false);
  };

  const addVideo = async () => {
    setVideoLoading(true);
    setVideoMsg('');
    try {
      if (!videoUrl.match(/^https?:\/\/(www\.)?youtube\.com\/.+/)) {
        setVideoMsg('Enter a valid YouTube URL');
        setVideoLoading(false);
        return;
      }
      const res = await fetchFn(`${API_URL}/api/admin/dashboard-videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: videoUrl })
      });
      const data = await res.json();
      if (data.success) {
        setVideoMsg('Video added!');
        setVideoUrl('');
        fetchVideos();
      } else {
        setVideoMsg(data.error || 'Failed to add video');
      }
    } catch (e) {
      setVideoMsg('Failed to add video');
    }
    setVideoLoading(false);
  };

  const deleteVideo = async (id) => {
    setVideoLoading(true);
    setVideoMsg('');
    try {
      await fetchFn(`${API_URL}/api/admin/dashboard-videos/${id}`, { method: 'DELETE' });
      setVideoMsg('Video deleted');
      fetchVideos();
    } catch (e) {
      setVideoMsg('Failed to delete video');
    }
    setVideoLoading(false);
  };

  // Summary stats
  const totalCodes = new Set(report.map(r => r.code)).size;
  const totalUses = report.length;
  const topReferrers = (() => {
    const counts = {};
    report.forEach(r => {
      if (!r.referrer) return;
      counts[r.referrer] = (counts[r.referrer] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3);
  })();

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: 24 }}>
      <h2 style={{ fontWeight: 800, fontSize: 24, color: '#5a4ff3', marginBottom: 18 }}>Referral Report</h2>
      {!hideConfig && (
        <>
          {/* Referral Reward Amount Section */}
          <div style={{ background: '#f3f4f6', borderRadius: 10, padding: 16, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontWeight: 600, color: '#5a4ff3' }}>Referral Reward Amount (₹):</span>
            <input type="number" min="0" value={rewardAmount} onChange={e => setRewardAmount(e.target.value)} style={{ width: 100, fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #ccc' }} disabled={rewardLoading} />
            <button className="gogig-btn" style={{ padding: '6px 18px' }} onClick={saveRewardAmount} disabled={rewardLoading}>{rewardLoading ? 'Saving...' : 'Save'}</button>
            {rewardMsg && <span style={{ marginLeft: 12, color: rewardMsg.includes('updated') ? 'green' : 'red' }}>{rewardMsg}</span>}
          </div>
          {/* Dashboard Videos Section */}
          <div style={{ background: '#f3f4f6', borderRadius: 10, padding: 16, marginBottom: 24 }}>
            <div style={{ fontWeight: 600, color: '#5a4ff3', marginBottom: 8 }}>Dashboard Videos (YouTube URLs)</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input type="text" placeholder="YouTube URL" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} style={{ width: 320, fontSize: 15, padding: 6, borderRadius: 6, border: '1px solid #ccc' }} disabled={videoLoading} />
              <button className="gogig-btn" style={{ padding: '6px 18px' }} onClick={addVideo} disabled={videoLoading}>{videoLoading ? 'Adding...' : 'Add'}</button>
              {videoMsg && <span style={{ marginLeft: 12, color: videoMsg.includes('add') || videoMsg.includes('deleted') ? 'green' : 'red' }}>{videoMsg}</span>}
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {videos.map(v => (
                <li key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 14 }}>{v.url}</span>
                  <button className="gogig-btn-outline" style={{ padding: '2px 10px', fontSize: 13 }} onClick={() => deleteVideo(v.id)} disabled={videoLoading}>Delete</button>
                </li>
              ))}
              {!videos.length && <li style={{ color: '#888', fontSize: 14 }}>No videos added yet.</li>}
            </ul>
          </div>
        </>
      )}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <select value={month} onChange={e => setMonth(e.target.value)} className="gogig-input" style={{ width: 120 }}>
          <option value="">All Months</option>
          {[...Array(12)].map((_, i) => (
            <option key={i+1} value={String(i+1).padStart(2, '0')}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
          ))}
        </select>
        <input type="number" placeholder="Year" value={year} onChange={e => setYear(e.target.value)} className="gogig-input" style={{ width: 100 }} />
        <button className="gogig-btn" onClick={fetchReport} disabled={loading}>{loading ? 'Loading...' : 'Fetch Report'}</button>
        <button className="gogig-btn-outline" onClick={handleDownloadCSV} disabled={loading || !report.length}>Download CSV</button>
      </div>
      {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
      <div style={{ marginBottom: 18 }}>
        <strong>Total Codes:</strong> {totalCodes} &nbsp; | &nbsp;
        <strong>Total Uses:</strong> {totalUses}
        {topReferrers.length > 0 && (
          <span> &nbsp; | &nbsp; <strong>Top Referrers:</strong> {topReferrers.map(([name, count]) => `${name} (${count})`).join(', ')}</span>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(90,79,243,0.07)', marginBottom: 18 }}>
          <thead>
            <tr style={{ background: '#f3f4f6' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Code</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Referrer</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Referred</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Company</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Type</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Mobile</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Email</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Used At</th>
            </tr>
          </thead>
          <tbody>
            {report.map((r, i) => (
              <tr key={i}>
                <td style={{ padding: 8 }}>{r.code}</td>
                <td style={{ padding: 8 }}>{r.referrer || '-'}</td>
                <td style={{ padding: 8 }}>{r.referred || '-'}</td>
                <td style={{ padding: 8 }}>{r.company || '-'}</td>
                <td style={{ padding: 8 }}>{r.type || '-'}</td>
                <td style={{ padding: 8 }}>{r.mobile || '-'}</td>
                <td style={{ padding: 8 }}>{r.email || '-'}</td>
                <td style={{ padding: 8 }}>{r.used_at ? new Date(r.used_at).toLocaleString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && !report.length && <div style={{ color: '#888' }}>No data for selected period.</div>}
    </div>
  );
}

export default AdminReferralReport; 